function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import React, { forwardRef, useContext } from 'react';
import styled, { ThemeContext, css } from 'styled-components';
import { fontStyle, mobile, breakpoints, themeLight } from '@naf/theme';
import { propValidationConfig } from './propValidationConfig';
export let TextVariant = /*#__PURE__*/function (TextVariant) {
  TextVariant["Display"] = "display";
  TextVariant["Header1"] = "header1";
  TextVariant["Header2"] = "header2";
  TextVariant["Header3"] = "header3";
  TextVariant["SubHeader"] = "subHeader";
  TextVariant["ArticleText"] = "articleText";
  TextVariant["ArticleTextHeader"] = "articleTextHeader";
  TextVariant["ArticleTextSubHeader"] = "articleTextSubHeader";
  TextVariant["ArticleTextLarge"] = "articleTextLarge";
  TextVariant["ArticleTextLargeHeader"] = "articleTextLargeHeader";
  TextVariant["Caption"] = "caption";
  TextVariant["CaptionHeader"] = "captionHeader";
  TextVariant["Ingress"] = "ingress";
  TextVariant["Blockquote"] = "blockquote";
  TextVariant["BlockquoteLarge"] = "blockquoteLarge";
  TextVariant["BodyText"] = "bodyText";
  TextVariant["BodyTextHeader"] = "bodyTextHeader";
  TextVariant["BodyTextSubHeader"] = "bodyTextSubHeader";
  TextVariant["Small"] = "small";
  TextVariant["Tiny"] = "tiny";
  TextVariant["CardHeader"] = "cardHeader";
  TextVariant["Tag"] = "tag";
  TextVariant["TagSmall"] = "tagSmall";
  TextVariant["Button"] = "button";
  TextVariant["ButtonSmall"] = "buttonSmall";
  TextVariant["DataLabel"] = "dataLabel";
  TextVariant["Giga"] = "giga";
  TextVariant["AnchorLink"] = "anchorLink";
  return TextVariant;
}({});
export let Breakpoint = /*#__PURE__*/function (Breakpoint) {
  Breakpoint["S"] = "s";
  Breakpoint["M"] = "m";
  Breakpoint["L"] = "l";
  Breakpoint["XL"] = "xl";
  return Breakpoint;
}({});
export const Text = /*#__PURE__*/forwardRef(({
  tag = null,
  variant = TextVariant.ArticleText,
  breakpoint = Breakpoint.S,
  ...rest
}, ref) => {
  const defaultTag = variant => {
    switch (variant) {
      case TextVariant.Display:
        return 'h1';
      case TextVariant.Header1:
        return 'h1';
      case TextVariant.Giga:
        return 'h1';
      case TextVariant.Header2:
        return 'h2';
      case TextVariant.Header3:
        return 'h3';
      case TextVariant.SubHeader:
        return 'h3';
      case TextVariant.ArticleTextHeader:
        return 'h4';
      case TextVariant.ArticleTextSubHeader:
        return 'h5';
      case TextVariant.ArticleTextLargeHeader:
        return 'h4';
      case TextVariant.CaptionHeader:
        return 'h5';
      case TextVariant.BodyTextHeader:
        return 'h6';
      case TextVariant.Tag:
        return 'span';
      case TextVariant.TagSmall:
        return 'span';
      case TextVariant.Button:
        return 'span';
      case TextVariant.ButtonSmall:
        return 'span';
      default:
        return 'p';
    }
  };
  const textBreakpoint = breakpoint => {
    switch (breakpoint) {
      case Breakpoint.S:
        return breakpoints.s;
      case Breakpoint.M:
        return breakpoints.m;
      case Breakpoint.L:
        return breakpoints.l;
      case Breakpoint.XL:
        return breakpoints.xl;
      default:
        return breakpoints.s;
    }
  };
  const theme = useContext(ThemeContext);
  const textStyles = {
    display: {
      'font-family': `${theme ? theme.fontFamilies.headers : themeLight.fontFamilies.headers}, -system-ui, system-ui, sans-serif`,
      ...fontStyle.headers.display
    },
    giga: {
      'font-family': `${theme ? theme.fontFamilies.headers : themeLight.fontFamilies.headers}, -system-ui, system-ui, sans-serif`,
      ...fontStyle.headers.giga
    },
    header1: {
      'font-family': `${theme ? theme.fontFamilies.headers : themeLight.fontFamilies.headers}, -system-ui, system-ui, sans-serif`,
      ...fontStyle.headers.header1
    },
    header2: {
      'font-family': `${theme ? theme.fontFamilies.headers : themeLight.fontFamilies.headers}, -system-ui, system-ui, sans-serif`,
      ...fontStyle.headers.header2
    },
    header3: {
      'font-family': `${theme ? theme.fontFamilies.headers : themeLight.fontFamilies.headers}, -system-ui, system-ui, sans-serif`,
      ...fontStyle.headers.header3
    },
    subHeader: {
      'font-family': `${theme ? theme.fontFamilies.default : themeLight.fontFamilies.default}, -system-ui, system-ui, sans-serif`,
      ...fontStyle.headers.subHeader
    },
    articleText: {
      'font-family': `${theme ? theme.fontFamilies.default : themeLight.fontFamilies.default}, -system-ui, system-ui, sans-serif`,
      ...fontStyle.article.articleText
    },
    articleTextHeader: {
      'font-family': `${theme ? theme.fontFamilies.default : themeLight.fontFamilies.default}, -system-ui, system-ui, sans-serif`,
      ...fontStyle.article.articleTextHeader
    },
    articleTextSubHeader: {
      'font-family': `${theme ? theme.fontFamilies.default : themeLight.fontFamilies.default}, -system-ui, system-ui, sans-serif`,
      ...fontStyle.article.articleTextSubHeader
    },
    articleTextLarge: {
      'font-family': `${theme ? theme.fontFamilies.default : themeLight.fontFamilies.default}, -system-ui, system-ui, sans-serif`,
      ...fontStyle.article.articleTextLarge
    },
    articleTextLargeHeader: {
      'font-family': `${theme ? theme.fontFamilies.default : themeLight.fontFamilies.default}, -system-ui, system-ui, sans-serif`,
      ...fontStyle.article.articleTextLargeHeader
    },
    caption: {
      'font-family': `${theme ? theme.fontFamilies.default : themeLight.fontFamilies.default}, -system-ui, system-ui, sans-serif`,
      ...fontStyle.article.caption
    },
    captionHeader: {
      'font-family': `${theme ? theme.fontFamilies.default : themeLight.fontFamilies.default}, -system-ui, system-ui, sans-serif`,
      ...fontStyle.article.captionHeader
    },
    ingress: {
      'font-family': `${theme ? theme.fontFamilies.quote : themeLight.fontFamilies.quote}, -system-ui, system-ui, sans-serif`,
      ...fontStyle.article.ingress
    },
    blockquote: {
      'font-family': `${theme ? theme.fontFamilies.quote : themeLight.fontFamilies.quote}, -system-ui, system-ui, sans-serif`,
      ...fontStyle.article.blockquote
    },
    blockquoteLarge: {
      'font-family': `${theme ? theme.fontFamilies.quote : themeLight.fontFamilies.quote}, -system-ui, system-ui, sans-serif`,
      ...fontStyle.article.blockquoteLarge
    },
    bodyText: {
      'font-family': `${theme ? theme.fontFamilies.default : themeLight.fontFamilies.default}, -system-ui, system-ui, sans-serif`,
      ...fontStyle.bodyText.bodyText
    },
    bodyTextHeader: {
      'font-family': `${theme ? theme.fontFamilies.default : themeLight.fontFamilies.default}, -system-ui, system-ui, sans-serif`,
      ...fontStyle.bodyText.bodyTextHeader
    },
    bodyTextSubHeader: {
      'font-family': `${theme ? theme.fontFamilies.default : themeLight.fontFamilies.default}, -system-ui, system-ui, sans-serif`,
      ...fontStyle.bodyText.bodyTextSubHeader
    },
    small: {
      'font-family': `${theme ? theme.fontFamilies.default : themeLight.fontFamilies.default}, -system-ui, system-ui, sans-serif`,
      ...fontStyle.bodyText.small
    },
    tiny: {
      'font-family': `${theme ? theme.fontFamilies.default : themeLight.fontFamilies.default}, -system-ui, system-ui, sans-serif`,
      ...fontStyle.bodyText.tiny
    },
    cardHeader: {
      'font-family': `${theme ? theme.fontFamilies.default : themeLight.fontFamilies.default}, -system-ui, system-ui, sans-serif`,
      ...fontStyle.componentText.cardHeader
    },
    tag: {
      'font-family': `${theme ? theme.fontFamilies.default : themeLight.fontFamilies.default}, -system-ui, system-ui, sans-serif`,
      ...fontStyle.componentText.tag
    },
    tagSmall: {
      'font-family': `${theme ? theme.fontFamilies.default : themeLight.fontFamilies.default}, -system-ui, system-ui, sans-serif`,
      ...fontStyle.componentText.tagSmall
    },
    button: {
      'font-family': `${theme ? theme.fontFamilies.default : themeLight.fontFamilies.default}, -system-ui, system-ui, sans-serif`,
      ...fontStyle.componentText.button
    },
    buttonSmall: {
      'font-family': `${theme ? theme.fontFamilies.default : themeLight.fontFamilies.default}, -system-ui, system-ui, sans-serif`,
      ...fontStyle.componentText.buttonSmall
    },
    dataLabel: {
      'font-family': `${theme ? theme.fontFamilies.default : themeLight.fontFamilies.default}, -system-ui, system-ui, sans-serif`,
      ...fontStyle.componentText.dataLabel
    },
    anchorLink: {
      'font-family': `${theme ? theme.fontFamilies.default : themeLight.fontFamilies.default}, -system-ui, system-ui, sans-serif`,
      ...fontStyle.article.articleText
    }
  };
  const mobileTextStyles = {
    ...textStyles,
    display: {
      'font-family': `${theme ? theme.fontFamilies.headers : themeLight.fontFamilies.headers}, -system-ui, system-ui, sans-serif`,
      ...mobile.headers.display
    },
    giga: {
      'font-family': `${theme ? theme.fontFamilies.headers : themeLight.fontFamilies.headers}, -system-ui, system-ui, sans-serif`,
      ...mobile.headers.giga
    },
    header1: {
      'font-family': `${theme ? theme.fontFamilies.headers : themeLight.fontFamilies.headers}, -system-ui, system-ui, sans-serif`,
      ...mobile.headers.header1
    },
    header2: {
      'font-family': `${theme ? theme.fontFamilies.headers : themeLight.fontFamilies.headers}, -system-ui, system-ui, sans-serif`,
      ...mobile.headers.header2
    },
    header3: {
      'font-family': `${theme ? theme.fontFamilies.headers : themeLight.fontFamilies.headers}, -system-ui, system-ui, sans-serif`,
      ...mobile.headers.header3
    },
    subHeader: {
      'font-family': `${theme ? theme.fontFamilies.default : themeLight.fontFamilies.default}, -system-ui, system-ui, sans-serif`,
      ...mobile.headers.subHeader
    },
    ingress: {
      'font-family': `${theme ? theme.fontFamilies.quote : themeLight.fontFamilies.quote}, -system-ui, system-ui, sans-serif`,
      ...mobile.article.ingress
    },
    blockquote: {
      'font-family': `${theme ? theme.fontFamilies.quote : themeLight.fontFamilies.quote}, -system-ui, system-ui, sans-serif`,
      ...mobile.article.blockquote
    },
    blockquoteLarge: {
      'font-family': `${theme ? theme.fontFamilies.quote : themeLight.fontFamilies.quote}, -system-ui, system-ui, sans-serif`,
      ...mobile.article.blockquoteLarge
    },
    cardHeader: {
      'font-family': `${theme ? theme.fontFamilies.default : themeLight.fontFamilies.default}, -system-ui, system-ui, sans-serif`,
      ...mobile.componentText.cardHeader
    }
  };
  return /*#__PURE__*/React.createElement(TextElement, _extends({
    ref: ref,
    as: tag || defaultTag(variant),
    styleVariant: textStyles[variant],
    mobileStyleVariant: mobileTextStyles[variant],
    breakpoint: textBreakpoint(breakpoint),
    variant: variant
  }, rest));
});
export default Text;
const TextElement = styled.p.withConfig(propValidationConfig).withConfig({
  displayName: "text__TextElement",
  componentId: "sc-4bdg82-0"
})(["", ";", " @media (max-width:", "){", ";}"], props => props.styleVariant, ({
  variant
}) => variant === TextVariant.AnchorLink && css(["cursor:pointer;border-bottom:1px dashed ", ";&:hover{border-bottom:none;color:", ";}"], ({
  theme
}) => theme.typography ? theme.typography.textLink : themeLight.typography.textLink, ({
  theme
}) => theme.typography ? theme.typography.textLink : themeLight.typography.textLink), props => props.breakpoint, props => props.mobileStyleVariant);
//# sourceMappingURL=text.js.map